import { MatrixClient, MatrixEvent, RelationType, Room } from 'matrix-js-sdk/src/matrix';

import { getIsAllowedEventType } from 'modules/MeetingChat/utils';
import { getMentionUserIdsFromContent, newMentionUserIds } from 'modules/Messenger/utils/resolveMentionDataForEdit';

type ReplaceInfo = {
  newContent: Record<string, unknown>;
  /** Index of the m.replace event in the live timeline. */
  replaceIdx: number;
};

/**
 * Scans the full timeline for `m.replace` events and returns a map of
 * original-event-id → latest replacement info.
 *
 * Using the timeline directly avoids relying on `event.replacingEvent()` being
 * pre-linked by the SDK (via `makeReplaced`), which may not have happened yet when
 * `RoomEvent.Timeline` fires.
 */
const buildReplaceMap = (timeline: MatrixEvent[]): Map<string, ReplaceInfo> => {
  const map = new Map<string, ReplaceInfo>();

  for (let i = 0; i < timeline.length; i++) {
    const event = timeline[i];
    if (!event.isRelation(RelationType.Replace)) continue;

    // getOriginalContent() always returns the event's own raw content, unaffected by SDK aggregation.
    const raw = event.getOriginalContent() as Record<string, unknown>;
    const relatesTo = raw['m.relates_to'] as { rel_type?: string; event_id?: string } | undefined;
    if (relatesTo?.rel_type !== RelationType.Replace || !relatesTo.event_id) continue;

    const newContent = raw['m.new_content'] as Record<string, unknown> | undefined;
    if (newContent === undefined) continue;

    // Keep only the latest replacement (highest index wins).
    const existing = map.get(relatesTo.event_id);
    if (!existing || i > existing.replaceIdx) {
      map.set(relatesTo.event_id, { newContent, replaceIdx: i });
    }
  }

  return map;
};

/** Effective mention user IDs (MSC3952): uses replace map when available, falls back to original content. */
export function getEffectiveMentionUserIds(event: MatrixEvent, replaceMap?: Map<string, ReplaceInfo>): string[] {
  const eventId = event.getId();
  if (eventId && replaceMap) {
    const info = replaceMap.get(eventId);
    if (info) return getMentionUserIdsFromContent(info.newContent);
  }

  // Fallback: use SDK's getContent() which in matrix-js-sdk 39.x already returns m.new_content
  // when _replacingEvent is set, or the original content otherwise.
  return getMentionUserIdsFromContent(event.getContent() as Record<string, unknown>);
}

const isAllowedMentionCandidate = (event: MatrixEvent, myUserId: string): boolean => {
  if (!getIsAllowedEventType(event.getType())) return false;
  if (event.getSender() === myUserId) return false;
  if (event.isRedacted()) return false;
  if (event.isRelation(RelationType.Replace)) return false;

  const raw = event.getOriginalContent() as Record<string, unknown>;
  if ((raw['m.relates_to'] as { rel_type?: string } | undefined)?.rel_type === RelationType.Replace) {
    return false;
  }

  return !event.isDecryptionFailure();
};

/**
 * Unread (since local read marker) allowed messages that effectively mention the current user.
 * Used to hydrate mention notifications after reload and for the conversation-list @ badge.
 */
export function getUnreadMentionEventsForUser(room: Room, matrixClient: MatrixClient): MatrixEvent[] {
  const myUserId = matrixClient.getUserId();
  if (!myUserId) return [];

  const timeline = room.getLiveTimeline().getEvents();
  const lastReadEventId = typeof room.getEventReadUpTo === 'function' ? room.getEventReadUpTo(myUserId) : null;

  let lastReadIdx = -1;
  if (lastReadEventId) {
    lastReadIdx = timeline.findIndex((e) => e.getId() === lastReadEventId);
  }
  const startIndex = lastReadIdx === -1 ? 0 : lastReadIdx + 1;

  const replaceMap = buildReplaceMap(timeline);
  const events: MatrixEvent[] = [];

  // Retroactive scan: already-read events where an edit newly adds a mention that B hasn't seen yet.
  if (lastReadIdx >= 0) {
    for (let i = 0; i <= lastReadIdx; i++) {
      const event = timeline[i];
      if (!isAllowedMentionCandidate(event, myUserId)) continue;

      const eventId = event.getId();
      if (!eventId) continue;

      const info = replaceMap.get(eventId);
      if (!info) continue;

      if (info.replaceIdx <= lastReadIdx) continue;

      const prevIds = getMentionUserIdsFromContent(event.getOriginalContent() as Record<string, unknown>);
      const nextIds = getMentionUserIdsFromContent(info.newContent);
      if (newMentionUserIds(prevIds, nextIds).includes(myUserId)) events.push(event);
    }
  }

  for (let i = startIndex; i < timeline.length; i++) {
    const event = timeline[i];
    if (!isAllowedMentionCandidate(event, myUserId)) continue;

    if (getEffectiveMentionUserIds(event, replaceMap).includes(myUserId)) events.push(event);
  }

  return events;
}

/**
 * True when an unread (since local read marker) allowed message effectively mentions the current user.
 * Used for conversation-list @ badge instead of stale NotificationCountType.Highlight alone.
 */
export function roomHasUnreadMentionForUser(room: Room, matrixClient: MatrixClient): boolean {
  return getUnreadMentionEventsForUser(room, matrixClient).length > 0;
}
